import { Component, inject, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AmplifyAuthenticatorModule, AuthenticatorService } from '@aws-amplify/ui-angular';
import { getCurrentUser } from 'aws-amplify/auth';
import { AuthService, sanitizeReturnUrl } from '../../../core';
import { ADMIN_GROUP } from '../../../core/guards/auth.guard';

@Component({
  imports: [AmplifyAuthenticatorModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None, // Needed so Amplify styles apply globally to amplify-authenticator component
})
export class LoginComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly authenticator = inject(AuthenticatorService);
  protected isNavigating = false;

  ngOnInit(): void {
    // Recover from Angular/Cognito desync: tokens still present but we landed on /login.
    void this.tryResumeExistingSession();
  }

  private async tryResumeExistingSession(): Promise<void> {
    try {
      const user = await getCurrentUser();
      if (this.isNavigating) {
        return;
      }
      this.authService.setAuthenticatedUser({
        userId: user.userId,
        username: user.username,
      });
      await this.navigateAfterAuth();
    } catch {
      // No Cognito session — show the Amplify sign-in form.
    }
  }

  protected completeLogin(): void {
    if (this.isNavigating) {
      console.log('Already navigating, skipping...');
      return;
    }

    console.log('completeLogin called');
    this.isNavigating = true;

    try {
      // Get the authenticated user from Amplify
      const user = this.authenticator.user;
      console.log('Authenticated user from Amplify:', user);

      if (user) {
        // Update the AuthService state with the authenticated user
        this.authService.setAuthenticatedUser(user);
        console.log('AuthService state updated');
      }

      void this.navigateAfterAuth().catch(error => {
        console.error('Navigation failed:', error);
        this.isNavigating = false;
      });
    } catch (error) {
      console.error('Error in completeLogin:', error);
      this.isNavigating = false; // Reset flag on error
    }
  }

  private async navigateAfterAuth(): Promise<void> {
    this.isNavigating = true;

    const queryReturn = this.route.snapshot.queryParams['returnUrl'] as
      | string
      | undefined;
    let returnUrl = sanitizeReturnUrl(queryReturn);
    if (!returnUrl) {
      const roles = await this.authService.getRoles();
      returnUrl = roles.includes(ADMIN_GROUP) ? '/simplify' : '/guardian';
    }
    console.log('Navigating to:', returnUrl);
    await this.router.navigateByUrl(returnUrl);
    console.log('Navigation completed successfully');
  }

  protected handleAuthenticated(event: any): void {
    console.log('Authenticated event received:', event);
    // The event contains the user object, so we can use it directly
    if (event && event.user) {
      // Update the AuthService state with the authenticated user
      this.authService.setAuthenticatedUser(event.user);
      console.log('AuthService state updated from event');
    }

    // Call the existing completeLogin logic
    this.completeLogin();
  }

  protected setAuthenticatedAndRedirect(user: any): boolean {
    // This method will be called by the template when it renders
    // We need to defer the signal update to avoid the NG0600 error
    setTimeout(() => {
      if (!this.isNavigating) {
        console.log('Setting authenticated user and redirecting:', user);
        this.completeLogin();
      }
    }, 0);

    // Return true to render the ng-container (it's hidden anyway)
    return true;
  }
}
